// Products (design/screens/products.html; UC-CATALOGUE-003, -004): the tabs with their counts, the search — the starts of words
// from two characters, debounced — the filters (category, brand, supplier, Show inactive, Not in latest price list), all kept in
// the address, and the page of products in the tabs' order with the acting branch's cost, markup, price and stock beside each
// (BR-CATALOGUE-026). A row opens the product's panel at /products/{id}. A scan is a code and Enter in the search box: it is
// looked up as a serial, a barcode, an SKU, then a tag (BR-CATALOGUE-025); a tablet's camera reads a code into the same lookup.
// The list's search finds what the lookup finds — a serial number or a unit's tag, whole, lists its product (BR-CATALOGUE-024) —
// so a scanned serial's frame is the list's row as well as the panel's (the hands-on test of 21 Oct 2026; TC-CATALOGUE-145).
// Keyboard: / focuses the search, ↑↓ move, Enter opens (or, after typing, looks the code up), Esc clears, N is a new product.
// Each row has a tick; Export prices (CSV) takes the ticked products, or with none ticked the tab and its filters, for a branch
// and a day (UC-PRICING-012). Import price list opens the price-list import's first step for an Owner or Admin (UC-IMPORTS-001
// A1; BR-IMPORTS-001); the page head's lab-price-list chip is pricing's and is left out (design/DESIGN.md).
import { PRODUCT_CATEGORIES, TAB_OF_CATEGORY, type ProductCategory, type ProductListItem, type ProductLookupResult, type ProductSummary, type SupplierListItem } from "@visionx/contracts";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { products, type ProductFilters } from "../api/endpoints.catalogue";
import { suppliers } from "../api/endpoints.purchasing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { decodeBarcodePhoto, touchDevice } from "../lib/barcode-camera";
import { startDownload } from "../lib/download";
import { formatDate, formatMoney, formatTime } from "../lib/format";
import { useOneStore } from "../session/one-store";
import { can, useSessionView } from "../session/store";
import { inBurst, scanCollector } from "../shell/key-burst";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, Chip, Empty, ErrorAlert, Loading, Modal, Pager, Panel, Select } from "../ui/bits";
import { Icon } from "../ui/icons";
import {
  BrandPicker, COST_SOURCE_WORD, InactiveChip, LIST_TABS, ProductPicker, SupplierPicker, TAB_CATEGORIES, TAB_LABEL, andList, categoryLabel, markupCell, stockCell, tabLabelOf, type ListTab,
} from "./CatalogueShared";
import { useImportAccess } from "./ImportsShared";
import { ExportPricesModal, type PriceSelection } from "./ProductPricing";
import { NoAccessPanel, PageHead } from "./common";
import { BarcodeRefusal, ProductPanel, barcodeKindOf, type ScannedSerial } from "./ProductPanel";
import { useDebouncedValue } from "./PatientsShared";

const PAGE = 50;
const isTab = (v: string | null): v is ListTab => LIST_TABS.some(([t]) => t === v);
const isCategory = (v: string | null): v is ProductCategory => (PRODUCT_CATEGORIES as readonly string[]).includes(v ?? "");
const isTyping = (t: EventTarget | null): boolean => t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
const isControl = (t: EventTarget | null): boolean => t instanceof HTMLElement && t.closest("a[href], button, summary, [role='button'], [role='link'], [role='option'], [role='tab'], [role='checkbox'], [role='combobox']") !== null;

/** What a product page hands the list when it opens: a notice, the save's warnings, a serial a scan found, the page it was on. */
export interface ProductsState { notice?: string | undefined; warnings?: string[] | undefined; scanned?: ScannedSerial | undefined; cursors?: string[] | undefined }

export function Products() {
  const s = useSessionView();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const { id: selectedId } = useParams();
  const block = useWriteBlock();
  const online = block === null;
  const canWrite = can(s, "catalogue.product.write");
  const canRules = can(s, "catalogue.rules.write");
  // UC-PRICING-012: every role reads prices, so every role may export them (BR-PRICING-024).
  const canExport = can(s, "pricing.price.read");
  const canImport = useImportAccess().write;
  const branch = s?.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s?.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? null;
  // A practice of one store: its catalogue is its store's, and the page says nothing of branches it does not have (UC-CATALOGUE-003
  // A11; DVN's answers of 28 Oct 2026, change 13).
  const oneStore = useOneStore();
  const [sp, setSp] = useSearchParams();
  const tab: ListTab = isTab(sp.get("tab")) ? (sp.get("tab") as ListTab) : "all";
  const rawCategory = sp.get("category");
  const category = isCategory(rawCategory) && (tab === "all" || TAB_OF_CATEGORY[rawCategory] === tab) ? rawCategory : undefined;
  const supplierId = sp.get("supplier") ?? undefined;
  const brand = sp.get("brand") ?? undefined;
  const includeInactive = sp.get("inactive") === "true";
  const flagged = sp.get("flagged") === "true";
  const urlQ = sp.get("q") ?? "";
  const [typed, setTyped] = useState(urlQ);
  const q = useDebouncedValue(typed.trim(), 300);
  useEffect(() => {
    if ((sp.get("q") ?? "") === q) return;
    setSp((p) => { const n = new URLSearchParams(p); if (q) n.set("q", q); else n.delete("q"); return n; }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const searching = q.length >= 2;
  const categories = category ? [category] : tab === "all" ? undefined : TAB_CATEGORIES[tab];
  const filters: ProductFilters = { q: searching ? q : undefined, category: categories, supplierId, brand, includeInactive, notInLatestPriceList: flagged };
  // The page comes back with the list from Edit (UC-CATALOGUE-002 step 1, TC-CATALOGUE-130); a change of filter starts at page 1.
  const [cursors, setCursors] = useState<string[]>(() => (location.state as ProductsState | null)?.cursors ?? []);
  const cursor = cursors[cursors.length - 1];
  const filterKey = JSON.stringify(filters);
  const shownFilter = useRef(filterKey);
  useEffect(() => { if (shownFilter.current === filterKey) return; shownFilter.current = filterKey; setCursors([]); }, [filterKey]);
  const list = useQuery({
    queryKey: ["products", "list", filters, cursor ?? ""],
    queryFn: () => products.list(filters, { limit: PAGE, cursor, count: cursor === undefined }),
    placeholderData: keepPreviousData,
  });
  // The counts of the first page stay while a later page is shown.
  const [counts, setCounts] = useState(list.data?.data.counts);
  useEffect(() => { if (list.data?.data.counts) setCounts(list.data.data.counts); }, [list.data]);
  const supplierRead = useQuery({ queryKey: ["supplier", supplierId], queryFn: () => suppliers.get(supplierId!), enabled: supplierId !== undefined, staleTime: 60_000 });
  const supplierValue = supplierId ? { id: supplierId, name: supplierRead.data?.data.name ?? "" } : null;
  const rows = list.data?.data.items ?? [];
  // The ticked products (UC-PRICING-012 step 1), kept across pages and filters until Untick all.
  const [ticked, setTicked] = useState<ReadonlySet<string>>(() => new Set());
  const tick = (id: string, on: boolean) => setTicked((x) => { const n = new Set(x); if (on) n.add(id); else n.delete(id); return n; });
  const [exporting, setExporting] = useState<PriceSelection | null>(null);

  const state = (location.state ?? null) as ProductsState | null;
  const [notice, setNotice] = useState<{ tone: "ok" | "warn" | "crit" | "info"; text: ReactNode; role?: "status" | undefined } | null>(null);
  const [scanned, setScanned] = useState<ScannedSerial | null>(state?.scanned ?? null);
  useEffect(() => {
    if (!state) return;
    if (state.notice) setNotice({ tone: state.warnings && state.warnings.length > 0 ? "warn" : "ok", text: <><b>{state.notice}</b>{state.warnings?.map((w) => <div key={w}>{w}</div>)}</> });
    if (state.scanned) setScanned(state.scanned);
    navigate(location.pathname + location.search, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // The keyboard's row: the open product's row while it is on the page, else the first.
  const [hi, setHi] = useState(0);
  const arrowed = useRef(false);
  useEffect(() => {
    const at = rows.findIndex((r) => r.id === selectedId);
    setHi(at >= 0 ? at : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, selectedId]);
  // The row the arrows reach is scrolled into view (UC-CATALOGUE-003 A1), never the page's scroll moved otherwise.
  useEffect(() => {
    if (!arrowed.current) return;
    document.querySelector(`#s-products tr[data-row="${hi}"]`)?.scrollIntoView?.({ block: "nearest" });
  }, [hi]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const hiRef = useRef(hi);
  hiRef.current = hi;
  const input = useRef<HTMLInputElement>(null);
  const keepSearch = sp.toString() ? `?${sp.toString()}` : "";
  const open = (id: string, extra?: ProductsState) => navigate(`/products/${id}${keepSearch}`, extra ? { state: extra } : undefined);
  const setParam = (changes: Record<string, string | null>) => {
    setSp((p) => { const n = new URLSearchParams(p); for (const [k, v] of Object.entries(changes)) { if (v) n.set(k, v); else n.delete(k); } return n; }, { replace: true });
  };
  const clearSearch = () => { setTyped(""); setLookupMiss(null); };

  // --- the scan lookup (UC-CATALOGUE-004) ---------------------------------------------------------------------------------
  const [lookupMiss, setLookupMiss] = useState<string | null>(null);
  const [choices, setChoices] = useState<ProductLookupResult | null>(null);
  const [linking, setLinking] = useState<string | null>(null);
  // Scans sent back to back are looked up one after the other, the latest waiting while one is out — never dropped, never run
  // together: the box's code is selected as its lookup starts and again when it ends, so the next scan replaces it (the review
  // of 18 Oct 2026, D1-8; TC-INVENTORY-152).
  const looking = useRef(false);
  const waiting = useRef<string | null>(null);
  const openRef = useRef(open);
  openRef.current = open;
  const lookup = async (code: string, o: { refocus: boolean } = { refocus: true }) => {
    const c = code.trim();
    if (!c) return;
    if (o.refocus && input.current?.value.trim() === c) input.current.select();
    if (looking.current) { waiting.current = c; return; }
    looking.current = true;
    let next: string | null = c;
    try {
      while (next !== null) {
        const now: string = next;
        waiting.current = null;
        setLookupMiss(null);
        setChoices(null);
        try {
          const { data } = await products.lookup(now);
          const scannedOf = (i: ProductLookupResult["items"][number]): ScannedSerial | undefined => (i.serial ? { productId: i.product.id, code: now, serial: i.serial } : undefined);
          if (data.items.length === 1) {
            const only = data.items[0]!;
            setScanned(scannedOf(only) ?? null);
            openRef.current(only.product.id, { scanned: scannedOf(only) });
          } else setChoices(data);
        } catch (err) {
          if (isApiError(err) && err.code === "NOT_FOUND") setLookupMiss(now);
          else setNotice({ tone: "crit", text: describeError(err) });
        }
        next = waiting.current;
      }
    } finally {
      looking.current = false;
      // The search box takes the focus back with its code selected (not after a tablet camera's read: the keyboard would open).
      if (o.refocus) setTimeout(() => { input.current?.focus(); input.current?.select(); }, 0);
    }
  };
  const lookupRef = useRef(lookup);
  lookupRef.current = lookup;
  const [touch] = useState(touchDevice);
  const camera = useRef<HTMLInputElement>(null);
  const fromCamera = async (file: File | undefined) => {
    if (camera.current) camera.current.value = "";
    if (!file) return;
    try {
      const code = await decodeBarcodePhoto(file);
      if (!code) { setNotice({ tone: "warn", text: <><b>No barcode was read.</b> Hold the code flat and close to the camera, or type it.</> }); return; }
      setTyped(code);
      await lookup(code, { refocus: false });
    } catch (err) { setNotice({ tone: "warn", text: <><b>This device cannot read barcodes from the camera.</b> {err instanceof Error ? err.message : ""} Type the code or use a scanner.</> }); }
  };

  // Capture phase: `/` reaches this list's own search box before the shell focuses the top bar's.
  useEffect(() => {
    // A scan that lands while the focus is on no field — a row was clicked — is a scan all the same: its code goes into the search
    // box and is looked up, and none of its keys is a shortcut — its Enter opens no row, an `n` in it no New product (the review
    // of 18 Oct 2026, D1-2; TC-INVENTORY-151). A person's keys act as before.
    const collect = scanCollector();
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      const typing = isTyping(e.target);
      const inBox = e.target === input.current;
      if (!typing) {
        const code = collect.take(e);
        if (code !== null) {
          e.preventDefault();
          e.stopPropagation();
          setTyped(code);
          arrowed.current = false;
          void lookupRef.current(code);
          return;
        }
        if ((e.key.length === 1 || e.key === "Enter") && inBurst(e)) return;
      }
      if (e.key === "/" && !typing) { e.preventDefault(); e.stopPropagation(); input.current?.focus(); input.current?.select(); return; }
      if (typing && !inBox) return;
      const current = rowsRef.current;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (!inBox && isControl(e.target)) return;
        e.preventDefault();
        arrowed.current = true;
        const step = e.key === "ArrowDown" ? 1 : -1;
        setHi((i) => Math.min(Math.max(i + step, 0), Math.max(current.length - 1, 0)));
      } else if (e.key === "Enter") {
        if (!inBox && isControl(e.target)) return;
        const text = input.current?.value.trim() ?? "";
        // A code and Enter — a scanner's, or a code typed — is looked up; after the arrows, Enter opens the highlighted row.
        if (inBox && text !== "" && !arrowed.current) { e.preventDefault(); void lookupRef.current(text); return; }
        const r = current[hiRef.current];
        if (r) { e.preventDefault(); open(r.id); }
      } else if (e.key === "Escape") {
        if (inBox) { e.preventDefault(); clearSearch(); }
      } else if (!typing && (e.key === "n" || e.key === "N")) {
        if (canWrite && online) { e.preventDefault(); navigate("/products/new"); }
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, canWrite, online, keepSearch]);

  // UC-PRICING-012 step 1, A1: what Export prices takes — the ticked products, else the active products of the tab and its filters.
  const selection = (): PriceSelection => {
    if (ticked.size > 0) return { productIds: [...ticked], words: `The ${ticked.size} ticked product${ticked.size === 1 ? "" : "s"}` };
    const words = [
      tab === "all" ? "Every active product" : `The ${TAB_LABEL[tab]} tab's active products`, category ? `(${categoryLabel(category)})` : null,
      searching ? `matching “${q}”` : null, supplierId ? `from ${supplierValue?.name || "the supplier"}` : null, brand ? `of ${brand}` : null,
    ].filter((w): w is string => w !== null).join(" ");
    const unapplied = [includeInactive ? "Show inactive" : null, flagged ? "Not in latest price list" : null].filter((w): w is string => w !== null);
    return {
      words, categories: categories ?? PRODUCT_CATEGORIES, q: searching ? q : undefined, supplierId, brand,
      unapplied: unapplied.length > 0 ? `${andList(unapplied)} ${unapplied.length === 1 ? "does" : "do"} not narrow an export: it takes the tab's active products with its search, supplier and brand. Tick products to export exactly those.` : undefined,
    };
  };

  const tabCount = (t: ListTab): number | undefined => (counts ? (t === "all" ? counts.all : counts[t]) : undefined);
  const first = cursors.length * PAGE + 1;
  const total = category && tab === "lenses" ? undefined : tabCount(tab);
  const denied = list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED";
  const hasFilters = typed !== "" || supplierId !== undefined || brand !== undefined || includeInactive || flagged || category !== undefined;
  const categoryOptions = tab === "all" ? PRODUCT_CATEGORIES : TAB_CATEGORIES[tab];

  return (
    <section className="screen" id="s-products">
      <div className="content">
        <PageHead
          title="Products"
          sub={oneStore && branch ? `Catalogue for ${branch}` : branch ? `Catalogue for ${branch} · prices and stock are per branch` : "One catalogue for every branch · prices and stock are per branch"}
          actions={<>
            {canExport && <button className="btn" type="button" disabled={!online} title={block ? WRITE_BLOCK_TITLE[block] : ticked.size > 0 ? `Exports the ${ticked.size} ticked` : "Exports this tab as filtered"} onClick={() => setExporting(selection())}>Export prices (CSV)</button>}
            <Link className="btn" to="/products/lens-options">Lens options</Link>
            {canImport && <Link className="btn" to="/price-import/new">Import price list</Link>}
            {canWrite && <button className="btn primary" type="button" disabled={!online} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => navigate("/products/new")}><Icon name="plus" />New product</button>}
          </>}
        />
        {notice && <Alert tone={notice.tone} role={notice.role} actions={<button className="btn sm" type="button" onClick={() => setNotice(null)}>Dismiss</button>}>{notice.text}</Alert>}
        <div className="tabs" role="tablist" aria-label="Categories">
          {LIST_TABS.map(([t, label]) => (
            <a key={t} href={`/products${t === "all" ? "" : `?tab=${t}`}`} role="tab" aria-selected={t === tab} className={t === tab ? "active" : ""}
              onClick={(e) => { e.preventDefault(); setParam({ tab: t === "all" ? null : t, category: null }); }}>
              {label}{tabCount(t) !== undefined && <span className="cnt">{tabCount(t)}</span>}
            </a>
          ))}
        </div>
        <div className="toolbar">
          <div className="input w-search">
            <Icon name="search" />
            <input ref={input} type="search" aria-label="Search products" placeholder="Search SKU, product, brand, barcode or serial…" autoComplete="off" value={typed}
              onChange={(e) => { setTyped(e.target.value); arrowed.current = false; setLookupMiss(null); }} />
            <kbd>/</kbd>
          </div>
          {touch && (
            <>
              <input ref={camera} type="file" accept="image/*" capture="environment" hidden aria-hidden="true" tabIndex={-1} data-testid="scan-camera" onChange={(e) => void fromCamera(e.target.files?.[0])} />
              <button className="btn" type="button" onClick={() => camera.current?.click()}><Icon name="camera" />Scan with camera</button>
            </>
          )}
          <Select aria-label="Category" boxClass="w-cat" value={category ?? ""} disabled={tab !== "all" && categoryOptions.length === 1}
            onChange={(e) => { const c = e.target.value; setParam(isCategory(c) ? { category: c, tab: TAB_OF_CATEGORY[c] } : { category: null }); }}>
            <option value="">{tab === "all" ? "Category · All" : `Category · ${TAB_LABEL[tab]}`}</option>
            {categoryOptions.length > 1 && categoryOptions.map((c) => <option key={c} value={c}>Category · {categoryLabel(c)}</option>)}
          </Select>
          <BrandPicker ariaLabel="Brand" boxClass="w-sel" value={brand ?? null} onPick={(b) => setParam({ brand: b })} />
          <SupplierPicker bare ariaLabel="Supplier" boxClass="w-sel" status="All" placeholder="Supplier · All" value={supplierValue} onPick={(x: SupplierListItem | null) => setParam({ supplier: x?.id ?? null })} />
          <Checkbox checked={includeInactive} onChange={(v) => setParam({ inactive: v ? "true" : null })}>Show inactive</Checkbox>
          <Checkbox checked={flagged} onChange={(v) => setParam({ flagged: v ? "true" : null })}>Not in latest price list</Checkbox>
          {ticked.size > 0 && <span className="row c gap-8"><b className="small">{ticked.size} ticked</b><button className="btn sm" type="button" onClick={() => setTicked(new Set())}>Untick all</button></span>}
          {tab === "services" && canRules && <Link className="btn sm" to="/products/combination-rules">Combination rules</Link>}
          <span className="right small faint" aria-live="polite">{list.data ? (total !== undefined ? `${cursors.length === 0 || rows.length === 0 ? rows.length : `${first}–${first + rows.length - 1}`} of ${total} products` : `${rows.length} shown`) : ""}</span>
        </div>
        {lookupMiss !== null && (
          <Alert tone="warn" role="alert" actions={<>
            <button className="btn sm" type="button" onClick={() => setLookupMiss(null)}>Search with the code</button>
            {canWrite && <button className="btn sm" type="button" disabled={!online} onClick={() => setLinking(lookupMiss)}>Link this barcode to a product</button>}
          </>}>
            <b>No product or serial has the code <span className="mono">{lookupMiss}</span>.</b>
          </Alert>
        )}
        {choices && (
          <Panel title={`${choices.items.length} carry the code ${choices.code}`} meta="matched on the tag · choose one" tight>
            <div className="list">
              {choices.items.map((i) => (
                <div key={`${i.product.id}-${i.serial?.id ?? ""}`} className="item">
                  <div className="grow"><div className="primary"><span className="mono">{i.product.sku}</span> {i.product.displayName}</div>{i.serial && <div className="sub">{i.serial.serialNumber ? <>Serial <span className="mono">{i.serial.serialNumber}</span> · </> : "A unit · "}{i.serial.branchName}</div>}</div>
                  <button className="btn sm" type="button" onClick={() => { setChoices(null); const sc = i.serial ? { productId: i.product.id, code: choices.code, serial: i.serial } : undefined; setScanned(sc ?? null); open(i.product.id, { scanned: sc }); }}>Open</button>
                </div>
              ))}
            </div>
          </Panel>
        )}
        {list.isPending && <Loading what="products" />}
        {list.isError && (denied ? <NoAccessPanel page="Products" error={list.error} /> : <ErrorAlert error={list.error} />)}
        {list.data && (
          <Panel tight foot={<>
            <span className="small faint">{oneStore ? "Inactive products stay on every document and report." : "One catalogue for every branch · inactive products stay on every document and report"}</span>
            <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data.data.nextCursor!])} />
          </>}>
            {rows.length === 0 ? (
              <Empty title={searching ? `No products match “${q}”.` : "No products match these filters."} actions={<>
                {hasFilters && <button className="btn" type="button" onClick={() => { clearSearch(); setSp(tab === "all" ? new URLSearchParams() : new URLSearchParams({ tab }), { replace: true }); }}>{searching ? "Clear search" : "Clear filters"}</button>}
                {!hasFilters && canWrite && online && <Link className="btn primary" to="/products/new">New product</Link>}
              </>}>
                {searching ? "A search matches the start of a word of the SKU, name, brand, supplier code, model number, tag or NAPPI and tariff codes, or a whole barcode, serial number or unit's tag."
                  // With the flag filter on, its inactive products are listed already (UC-CATALOGUE-003 A4): Show inactive changes nothing.
                  : flagged ? "Flagged products are listed whether active or inactive." : includeInactive ? "" : "Inactive products are hidden; Show inactive lists them."}
              </Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>SKU</th><th>Product</th><th>Supplier</th><th className="wide-only">Category</th><th className="r wide-only">Cost</th><th className="r wide-only">Markup</th><th className="r">Price{branch ? ` (${branch})` : ""}</th><th>Stock{branch ? ` (${branch})` : ""}</th><th className="xl-only">Codes</th></tr></thead>
                  <tbody>
                    {rows.map((r, i) => <Row key={r.id} r={r} index={i} highlighted={i === hi} onOpen={() => open(r.id)} branch={branch} tick={canExport ? { checked: ticked.has(r.id), onChange: (v) => tick(r.id, v) } : null} />)}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        {/* Keyed by the product: another product opened — Back or Forward under a dialog, the list beside a field — starts its panel afresh,
            closing what was open on it, so nothing typed for one product is sent for another (UC-CATALOGUE-003 A12; the verification of
            the third fix round, 31 Oct 2026, C3V-ONE-1 and C3V-ONE-5). */}
        {selectedId && <ProductPanel key={selectedId} id={selectedId} scanned={scanned?.productId === selectedId ? scanned : null} onNotice={setNotice} listPlace={{ search: keepSearch, cursors }} />}
        <div className="kbd-hints">
          <span><kbd>↑</kbd><kbd>↓</kbd> move</span>
          <span><kbd>Enter</kbd> open · after a code, look it up</span>
          <span><kbd>/</kbd> search</span>
          {canWrite && <span><kbd>N</kbd> new product</span>}
          <span><kbd>Esc</kbd> clear search</span>
        </div>
        {exporting !== null && (
          <ExportPricesModal selection={exporting} onClose={() => setExporting(null)} onDone={(r) => {
            setExporting(null);
            startDownload(r.downloadUrl, r.fileName);
            setNotice({ tone: "ok", role: "status", text: <><b>Export ready.</b> <span className="mono">{r.fileName}</span> · {r.rowCount} product{r.rowCount === 1 ? "" : "s"} · the download has started; <a href={r.downloadUrl} download={r.fileName}>download it again</a> until {formatTime(r.expiresAt)}. Recorded in the audit log.</> });
          }} />
        )}
        {linking !== null && (
          <LinkBarcodeModal code={linking} onClose={() => setLinking(null)} onLinked={(p) => {
            setLinking(null);
            setLookupMiss(null);
            void qc.invalidateQueries({ queryKey: ["product", p.id] });
            setNotice({ tone: "ok", text: <><b><span className="mono">{linking}</span> is now a barcode of {p.sku} {p.displayName}.</b> A scan of it opens the product.</> });
            open(p.id);
          }} />
        )}
      </div>
    </section>
  );
}

function Row({ r, index, highlighted, onOpen, branch, tick }: { r: ProductListItem; index: number; highlighted: boolean; onOpen: () => void; branch: string | null; tick: { checked: boolean; onChange: (v: boolean) => void } | null }) {
  const f = r.atBranch;
  const price = f?.priceCents ?? r.priceCents;
  const chips = (r.status === "Inactive" || r.notInLatestPriceList) && (
    <div className="row c gap-8">
      {r.status === "Inactive" && <InactiveChip />}
      {r.notInLatestPriceList && <Chip tone="warn">Not in latest price list{r.notInPriceListSince ? ` · ${formatDate(r.notInPriceListSince)}` : ""}</Chip>}
    </div>
  );
  return (
    <tr data-row={index} className={`clickable${highlighted ? " sel" : ""}`} aria-selected={highlighted} onClick={onOpen}>
      <td className="mono nw"><Link to={`/products/${r.id}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(); }}>{r.sku}</Link></td>
      {/* The tick sits beside the product's name, its chips below at the cell's edge: the Product column is as wide as a chip
          already, so the columns A10 keeps at a laptop's and a tablet's width still fit (UC-CATALOGUE-003 A10). Its click ticks
          and never opens the row. */}
      <td>
        {tick ? <div className="row c gap-8"><span onClick={(e) => e.stopPropagation()}><Checkbox ariaLabel={`Tick ${r.sku}`} checked={tick.checked} onChange={tick.onChange} /></span><span>{r.displayName}</span></div> : r.displayName}
        {chips}
      </td>
      <td>{r.supplier ? r.supplier.name : <span className="faint">—</span>}</td>
      <td className="wide-only">{tabLabelOf(r.category)}</td>
      {/* A made-to-order lens's cost names the leg that gave it, from these figures — "lab price" or "supplier price" — under the
          amount, as the prototype words a money cell's note; a stock line's average names none (UC-CATALOGUE-003 A14 step 2). */}
      <td className={`money wide-only${f?.costCents == null ? " faint" : ""}`}>{f?.costCents == null ? "—" : <>{formatMoney(f.costCents)}{f.costSource ? <div className="small faint">{COST_SOURCE_WORD[f.costSource]}</div> : null}</>}</td>
      <td className="r num wide-only">{markupCell(r, f, branch)}</td>
      <td className="money strong">{price === null ? <span className="faint">No price</span> : formatMoney(price)}</td>
      <td>{stockCell(r, f)}</td>
      <td className="xl-only">{r.codes.length === 0 ? <span className="faint">—</span> : r.codes.map((c) => <div key={c} className="mono small nw">{c}</div>)}</td>
    </tr>
  );
}
/** UC-CATALOGUE-007 A1: a scanned code no product has, linked to the product picked here — a stock role only. */
function LinkBarcodeModal({ code, onClose, onLinked }: { code: string; onClose: () => void; onLinked: (p: ProductSummary) => void }) {
  const [picked, setPicked] = useState<ProductSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // One key per product the code is linked to: a retry after a lost answer is the same request.
  const key = useMemo(() => newIdempotencyKey(), [picked?.id]);
  const submit = async () => {
    if (!picked || busy) return;
    setBusy(true);
    setError(null);
    try {
      await products.addBarcode(picked.id, { kind: barcodeKindOf(code), value: code }, key);
      onLinked(picked);
    } catch (err) {
      // Pressed again after a lost answer, answered as carried out: linked once, its answer not kept (UC-CATALOGUE-007 A7).
      if (carriedOut(err)) onLinked(picked); else setError(err);
    }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Link this barcode to a product" meta={<span className="mono">{code}</span>} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={!picked || busy}>{busy ? "Linking…" : "Link barcode"}</button>
    </>}>
      {error !== null && <Alert tone="crit"><b>Not linked.</b> <BarcodeRefusal error={error} /></Alert>}
      <ProductPicker label="Product" required value={picked} onPick={setPicked} allow={(p) => !p.trackSerials} keyTag="link-barcode"
        help="A product held without serials: a serialised product's barcode is each unit's serial." />
    </Modal>
  );
}
